import { Button } from '@/components/ui/shadcn/button';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { router } from '@inertiajs/react';
import { endOfMonth, endOfQuarter, endOfYear, format, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';
import * as React from 'react';

interface PeriodFilterProps {
    /** Current period (Inertia prop, or controlled local state). */
    dateRange: { from_date: string; to_date: string };
    /** Years that have data; merged with a generated fallback range. */
    availableYears?: number[];
    /** Notified while an Inertia navigation is in flight (for loading overlays). */
    onNavigatingChange?: (navigating: boolean) => void;
    /**
     * Controlled mode: when provided, the filter calls this with the new range
     * instead of navigating the page (for views that filter client-side).
     */
    onRangeChange?: (from: Date, to: Date) => void;
    className?: string;
}

/**
 * Shared period (year / quarter / quick-select) navigation used by both the
 * analytics modules and the homepage statistics dashboard, so the two stay
 * visually and behaviourally identical. Re-fetches the current page via Inertia
 * with from_date / to_date query params.
 */
export function PeriodFilter({ dateRange, availableYears, onNavigatingChange, onRangeChange, className }: PeriodFilterProps) {
    const { t } = useTranslations();

    const [selectedQuarter, setSelectedQuarter] = React.useState<string | null>(null);
    const [selectedYear, setSelectedYear] = React.useState<string | null>(null);
    const [activeQuickSelect, setActiveQuickSelect] = React.useState<'month' | 'quarter' | 'year' | null>(null);

    // Detect month / quarter / year selection from the active date range.
    React.useEffect(() => {
        const from = new Date(dateRange.from_date);
        const to = new Date(dateRange.to_date);
        const now = new Date();
        const eq = (a: Date, b: Date) => format(a, 'yyyy-MM-dd') === format(b, 'yyyy-MM-dd');

        if (eq(startOfMonth(from), from) && eq(endOfMonth(from), to)) {
            setSelectedQuarter(null);
            setSelectedYear(null);
            setActiveQuickSelect(eq(startOfMonth(now), from) && eq(endOfMonth(now), to) ? 'month' : null);
            return;
        }
        if (eq(startOfQuarter(from), from) && eq(endOfQuarter(from), to)) {
            setSelectedQuarter((Math.floor(from.getMonth() / 3) + 1).toString());
            setSelectedYear(from.getFullYear().toString());
            setActiveQuickSelect(eq(startOfQuarter(now), from) && eq(endOfQuarter(now), to) ? 'quarter' : null);
            return;
        }
        if (eq(startOfYear(from), from) && eq(endOfYear(from), to)) {
            setSelectedYear(from.getFullYear().toString());
            setSelectedQuarter(null);
            setActiveQuickSelect(eq(startOfYear(now), from) && eq(endOfYear(now), to) ? 'year' : null);
            return;
        }
        setSelectedQuarter(null);
        setSelectedYear(null);
        setActiveQuickSelect(null);
    }, [dateRange.from_date, dateRange.to_date]);

    const years = React.useMemo(() => {
        const currentYear = new Date().getFullYear();
        const fallback = Array.from({ length: 10 }, (_, i) => currentYear - i);
        return Array.from(new Set([...(availableYears ?? []), ...fallback])).sort((a, b) => b - a);
    }, [availableYears]);

    const navigate = (start: Date, end: Date) => {
        // Controlled mode: hand the range back to the parent (client-side filtering).
        if (onRangeChange) {
            onRangeChange(start, end);
            return;
        }
        router.get(
            window.location.pathname,
            { from_date: format(start, 'yyyy-MM-dd'), to_date: format(end, 'yyyy-MM-dd') },
            {
                preserveState: true,
                preserveScroll: true,
                onStart: () => onNavigatingChange?.(true),
                onFinish: () => onNavigatingChange?.(false),
            },
        );
    };

    const handleYearSelect = (year: number) => {
        setSelectedYear(year.toString());
        setSelectedQuarter(null);
        setActiveQuickSelect(null);
        navigate(startOfYear(new Date(year, 0, 1)), endOfYear(new Date(year, 11, 31)));
    };

    const handleQuarterSelect = (quarter: string) => {
        const year = selectedYear ? parseInt(selectedYear) : new Date().getFullYear();
        const base = new Date(year, (parseInt(quarter) - 1) * 3, 1);
        setSelectedQuarter(quarter);
        if (!selectedYear) {
            setSelectedYear(year.toString());
        }
        setActiveQuickSelect(null);
        navigate(startOfQuarter(base), endOfQuarter(base));
    };

    const handleThisMonth = () => {
        const now = new Date();
        setSelectedYear(null);
        setSelectedQuarter(null);
        setActiveQuickSelect('month');
        navigate(startOfMonth(now), endOfMonth(now));
    };

    const handleThisQuarter = () => {
        const now = new Date();
        setSelectedYear(now.getFullYear().toString());
        setSelectedQuarter((Math.floor(now.getMonth() / 3) + 1).toString());
        setActiveQuickSelect('quarter');
        navigate(startOfQuarter(now), endOfQuarter(now));
    };

    const handleThisYear = () => {
        const now = new Date();
        setSelectedYear(now.getFullYear().toString());
        setSelectedQuarter(null);
        setActiveQuickSelect('year');
        navigate(startOfYear(now), endOfYear(now));
    };

    const handleCustomRangeChange = (fromStr: string, toStr: string) => {
        let from = new Date(fromStr);
        let to = new Date(toStr);
        if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
            return;
        }
        if (from > to) {
            // Whichever endpoint the user just edited wins; clamp the other to match.
            if (fromStr !== dateRange.from_date) {
                to = from;
            } else {
                from = to;
            }
        }
        setSelectedYear(null);
        setSelectedQuarter(null);
        setActiveQuickSelect(null);
        navigate(from, to);
    };

    return (
        <div className={cn('flex flex-wrap items-center gap-3', className)}>
            <Select
                value={selectedYear || ''}
                onValueChange={(value) => {
                    if (value) {
                        handleYearSelect(parseInt(value));
                    }
                }}
            >
                <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder={t('analytics.crm_dashboard.filters.select_year', 'Select Year')} />
                </SelectTrigger>
                <SelectContent>
                    {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                            {year}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Select
                value={selectedQuarter || ''}
                onValueChange={(value) => {
                    if (value) {
                        handleQuarterSelect(value);
                    }
                }}
            >
                <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder={t('analytics.crm_dashboard.filters.select_quarter', 'Select Quarter')} />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="1">{t('analytics.crm_dashboard.filters.q1', 'Q1')}</SelectItem>
                    <SelectItem value="2">{t('analytics.crm_dashboard.filters.q2', 'Q2')}</SelectItem>
                    <SelectItem value="3">{t('analytics.crm_dashboard.filters.q3', 'Q3')}</SelectItem>
                    <SelectItem value="4">{t('analytics.crm_dashboard.filters.q4', 'Q4')}</SelectItem>
                </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
                <DatePicker
                    value={dateRange.from_date}
                    onValueChange={(value) => value && handleCustomRangeChange(value, dateRange.to_date)}
                    placeholder={t('homepage.analytics.date_range.from_date', 'From date')}
                    className="w-[150px]"
                />
                <DatePicker
                    value={dateRange.to_date}
                    onValueChange={(value) => value && handleCustomRangeChange(dateRange.from_date, value)}
                    placeholder={t('homepage.analytics.date_range.to_date', 'To date')}
                    className="w-[150px]"
                />
            </div>

            <div className="flex items-center gap-2">
                <Button variant={activeQuickSelect === 'month' ? 'default' : 'outline'} size="sm" onClick={handleThisMonth}>
                    {t('analytics.crm_dashboard.filters.this_month', 'This Month')}
                </Button>
                <Button variant={activeQuickSelect === 'quarter' ? 'default' : 'outline'} size="sm" onClick={handleThisQuarter}>
                    {t('analytics.crm_dashboard.filters.this_quarter', 'This Quarter')}
                </Button>
                <Button variant={activeQuickSelect === 'year' ? 'default' : 'outline'} size="sm" onClick={handleThisYear}>
                    {t('analytics.crm_dashboard.filters.this_year', 'This Year')}
                </Button>
            </div>
        </div>
    );
}
